import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import Chart from 'react-apexcharts';
import {
  AnalyticsFilters,
  AnalyticsKPICard,
  AnalyticsTable,
  ReportDownload
} from 'src/components/ocpi/analytics';
import {
  ANALYTICS_TABS,
  MOCK_DATA,
  REVENUE_TABLE_COLUMNS,
  PARTNER_TRANSACTIONS_TABLE_COLUMNS,
  TOP_LOCATIONS_TABLE_COLUMNS
} from 'src/components/ocpi/analytics/analytics.constants';
import { Loader } from 'src/components/Loader';
import { formatOcpiCurrency } from 'src/utils/formatTime';
import { getStartDateFromInterval } from 'src/utils/helper';
import { analyticsReportUrl } from 'src/react-query/endPoints';
// hooks
import { useDropdown } from 'src/hooks/useDropdown.hook';
import { useFetchOnDemand, useList } from 'src/hooks/useFetch.hook';
import useDownload from 'src/hooks/useDownload.hook';

// Using constants from analytics.constants.js

const OCPIAnalytics = () => {
  const [activeTab, setActiveTab] = useState("transactions");
  const [timeRange, setTimeRange] = useState("Last 7 Days");
  const [isCustomSelect, setIsCustomSelect] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(() => {
    const sevenDaysAgo = getStartDateFromInterval("Last 7 Days");
    return sevenDaysAgo.toLocaleDateString('en-CA');
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [partnerId, setPartnerId] = useState('all'); // Added partnerId state
  const theme = useTheme();
  
  // Use the date_filter dropdown from the system
  const { data: dateDD } = useDropdown("date_filter");

  // Analytics API - using existing hooks
  const { fetchData: mutateAnalytics, isLoading, data: analyticsData } = useFetchOnDemand("analytics", !1, !1);
  const { fetchData: mutateAnalyticsMetrics, isLoading: metricsLoading, data: analyticsMetricsData } = useFetchOnDemand("analyticsMetrics", !1, !1);
  const { data: analyticsTypes, loading: typesLoading } = useList("analyticsTypes");
  const { data: analyticsMetadata, loading: metadataLoading } = useList("analyticsMetadata");
  
  // Use the download hook for report downloads
  const { download: downloadReport, isDownloading: isDownloadingReport, downloadError } = useDownload(
    analyticsReportUrl,
    {
      onSuccess: (blob, fileName) => {
        console.log(`Report downloaded successfully: ${fileName}`);
      },
      onError: (error) => {
        console.error('Download failed:', error);
        alert(`Download failed: ${error.message}`);
      }
    }
  );

  // Function to fetch analytics data using multiple types
  const fetchAnalyticsData = useCallback(async (types, filters) => {
    try {
      const requestBody = {
        types: Array.isArray(types) ? types : [types],
        filters: {
          startDate: customStartDate,
          endDate: customEndDate,
          partnerId: partnerId,
          ...filters
        }
      };
      if (Array.isArray(types)) {
        await mutateAnalytics(requestBody);
      } else {
        await mutateAnalyticsMetrics({
          category: types,
          params: requestBody.filters
        });
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      // You could add a toast notification here for user feedback
    }
  }, [mutateAnalytics, customStartDate, customEndDate, partnerId]);

  // Function to fetch comprehensive dashboard data
  const fetchDashboardData = useCallback(async () => {
    await fetchAnalyticsData(['dashboard', 'revenue', 'transactions', 'partners', 'roaming'], { partnerId });
  }, []);

  // Fetch initial data when component mounts or filters change
  useEffect(() => {
    if (analyticsTypes && analyticsMetadata && !typesLoading && !metadataLoading) {
      fetchDashboardData();
    }
  }, [analyticsTypes, analyticsMetadata, typesLoading, metadataLoading, fetchDashboardData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangeTimeInterval = (value) => {
    setTimeRange(value);
    setIsCustomSelect(value === "Custom");
    
    if (value === "Custom") {
      // Keep existing custom dates
      return;
    }
    // Calculate dates based on selected time range
    const now = new Date();
    const startDate = getStartDateFromInterval(value);
    console.log(startDate);
    setCustomStartDate(startDate.toLocaleDateString('en-CA'));
    setCustomEndDate(now.toISOString().split('T')[0]);
  };

  const handlePartnerChange = (value) => {
    setPartnerId(value);
  };

  // Report download handler using the useDownload hook
  const handleReportDownload = async (downloadParams) => {
    const { reportType, format, filters } = downloadParams;
    
    // Prepare the request body for the API
    const requestBody = {
      reportType,
      format,
      filters: {
        startDate: customStartDate,
        endDate: customEndDate,
        partnerId: partnerId,
        ...filters
      }
    };

    const fileExtension = format === 'excel' ? 'xlsx' : format;
    // Use the download hook
    await downloadReport(requestBody, {
      fileName: `${reportType}_report_${customStartDate}_to_${customEndDate}.${fileExtension}`,
      fileExtension
    });
  };

  const { dashboard, revenue, transactions, roaming } = useMemo(() => {
    let analytics = { dashboard: {}, revenue: {}, transactions: {}, roaming: {} };
    if (analyticsData) {
        analytics = {
            dashboard: analyticsData.dashboard,
            revenue: {
              dailyData: analyticsData.revenue.summary,
              charts: analyticsData.revenue.chartData,
              revenueAnalysis: analyticsData.revenue.revenueAnalysis
            },
            transactions: {
              cdrs: analyticsData.transactions.transactionTypes[1],
              charts: analyticsData.transactions.charts
            },
            roaming: {
              kpis: analyticsData.roaming.kpis,
              trends: analyticsData.roaming.trends,
              charts: analyticsData.roaming.chartData,
              topLocations: analyticsData.roaming.topLocations
            }
        }
    }

    if (analyticsMetricsData && analyticsMetricsData.transactionTypes) {
      analytics = {
        ...analytics,
        transactions: {
          cdrs: analyticsMetricsData.transactionTypes[1],
          charts: analyticsMetricsData.charts
        },
      }
    }

    if (analyticsMetricsData && analyticsMetricsData.dailyData) {
      analytics = {
        ...analytics,
        revenue: {
          dailyData: analyticsMetricsData.summary,
          charts: analyticsMetricsData.chartData,
          revenueAnalysis: analyticsMetricsData.revenueAnalysis
        }
      }
    }

    if (analyticsMetricsData && analyticsMetricsData.kpis) {
      analytics = {
        ...analytics,
        roaming: {
          ...analyticsMetricsData,
          charts: analyticsMetricsData.chartData
        }
      }
    }
    
    return analytics
  }, [analyticsData, analyticsMetricsData]);
  // Using AnalyticsKPICard component instead of renderKPICard function
  const renderTabContent = () => {
    switch (activeTab) {
      case "transactions":
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Transaction Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive analysis of transaction volume, types, and patterns.
                </Typography>
              </Box>
              {/* <ReportDownload
                reportType="transactions"
                reportTitle="Download Transaction Report"
                downloadFunction={handleReportDownload}
                isDownloading={isDownloadingReport}
                downloadError={downloadError}
                filters={{
                  timeRange,
                  customStartDate,
                  customEndDate,
                  partnerId
                }}
                buttonVariant="outlined"
                buttonSize="small"
                iconOnly={false}
              /> */}
            </Box>
            
            <AnalyticsFilters
              title="Transaction Analytics Filters"
              analyticsType="transactions"
              timeRange={timeRange}
              onTimeRangeChange={handleChangeTimeInterval}
              isCustomSelect={isCustomSelect}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              dateOptions={dateDD || []}
              onApply={() => fetchAnalyticsData('transactions', { partnerId })}
              buttonSize="medium"
              analyticsTypes={analyticsTypes?.data?.analyticsTypes || []}
              analyticsMetadata={analyticsMetadata?.data || {}}
              loading={metricsLoading}
              partnerId={partnerId}
              onPartnerChange={handlePartnerChange}
            />
            
            {/* Transaction Overview Cards */}
            {metricsLoading || isLoading ? <Loader variant='skeleton' skeletonCount={4} skeletonVariant='kpi' /> : 
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Total Transactions"
                  value={transactions?.cdrs?.count || 0}
                  icon="eva:activity-fill"
                  color="primary"
                  subtitle="All time transaction count"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Success Rate"
                  value={transactions?.cdrs?.successRate || 0}
                  icon="eva:checkmark-circle-fill"
                  color="success"
                  unit="%"
                  subtitle="Overall transaction success rate"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Total Revenue"
                  value={formatOcpiCurrency(transactions?.cdrs?.totalRevenue || 0)}
                  icon="eva:layers-fill"
                  color="info"
                  subtitle="Total revenue generated"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Avg Response Time"
                  value={transactions?.cdrs?.avgResponseTime || 0}
                  icon="eva:clock-fill"
                  color="warning"
                  unit="s"
                  subtitle="System performance"
                />
              </Grid>
            </Grid>}

            {/* Transaction Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hourly Transaction Flow
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'bar', 
                          toolbar: { show: false },
                          height: 350,
                          stacked: false
                        },
                        xaxis: {
                          categories: transactions?.charts?.hourlyFlow?.categories || [] ,
                          labels: {
                            rotate: -45,
                            style: {
                              fontSize: '10px'
                            }
                          }
                        },
                        yaxis: {
                          title: { text: 'Transaction Count' },
                          labels: { formatter: (value) => value.toLocaleString() },
                          min: 0,
                          max: 80
                        },
                        colors: [theme.palette.primary.main, theme.palette.secondary.main],
                        legend: { 
                          position: 'top',
                          horizontalAlign: 'center'
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '70%',
                            borderRadius: 4
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          y: {
                            formatter: (value) => value.toLocaleString()
                          }
                        }
                      }}
                      series={[
                        {
                          name: 'Incoming',
                          data: transactions?.charts?.hourlyFlow?.incoming || Array.from({ length: 24 }, () => 0)
                        },
                        {
                          name: 'Outgoing',
                          data: transactions?.charts?.hourlyFlow?.outgoing || Array.from({ length: 24 }, () => 0)
                        }
                      ]}
                      type="bar"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} lg={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Transaction Types
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'donut', 
                          toolbar: { show: false },
                          height: 350
                        },
                        labels: (transactions?.charts?.transactionTypes?.labels || []).length > 0 
                          ? transactions.charts.transactionTypes.labels 
                          : ['Start Session', 'Stop Session', 'Unlock Connector', 'Reserve Connector'],
                        colors: [
                          theme.palette.success.main,
                          theme.palette.warning.main,
                          theme.palette.info.main,
                          theme.palette.secondary.main
                        ],
                        legend: { 
                          position: 'bottom',
                          fontSize: '12px'
                        },
                        plotOptions: {
                          donut: {
                            size: '60%',
                            labels: {
                              show: true,
                              total: {
                                show: true,
                                label: 'Total Transactions',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: theme.palette.text.primary
                              }
                            }
                          }
                        },
                        dataLabels: {
                          enabled: false,
                          formatter: (value, { seriesIndex }) => {
                            const types = transactions?.charts?.transactionTypes?.labels || ['Start Session', 'Stop Session', 'Unlock Connector', 'Reserve Connector'];
                            const data = transactions?.charts?.transactionTypes?.data || [0, 0, 0, 0];
                            return `${types[seriesIndex]}\n${data[seriesIndex].toLocaleString()}`;
                          },
                          style: {
                            fontSize: '11px',
                            fontWeight: 600
                          }
                        },
                        tooltip: {
                          y: {
                            formatter: (value, { seriesIndex }) => {
                              const types = transactions?.charts?.transactionTypes?.labels || ['Start Session', 'Stop Session', 'Unlock Connector', 'Reserve Connector'];
                              const data = transactions?.charts?.transactionTypes?.data || [0, 0, 0, 0];
                              return `${data[seriesIndex].toLocaleString()} transactions`;
                            }
                          }
                        }
                      }}
                      series={transactions?.charts?.transactionTypes?.data || [0, 0, 0, 0]}
                      type="donut"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      case "revenue":
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Revenue Analysis & Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive revenue breakdown, trends, and partner performance with detailed analytics.
                </Typography>
              </Box>
              {/* <ReportDownload
                reportType="revenue"
                reportTitle="Download Revenue Report"
                downloadFunction={handleReportDownload}
                isDownloading={isDownloadingReport}
                downloadError={downloadError}
                filters={{
                  timeRange,
                  customStartDate,
                  customEndDate,
                  partnerId
                }}
                buttonVariant="outlined"
                buttonSize="small"
                iconOnly={false}
                customFormats={[
                  {
                    type: 'pdf',
                    label: 'PDF Revenue Report',
                    icon: 'eva:file-text-fill',
                    description: 'Formatted revenue report with charts',
                    color: 'error'
                  },
                  {
                    type: 'excel',
                    label: 'Excel Revenue Data',
                    icon: 'eva:file-fill',
                    description: 'Revenue data for financial analysis',
                    color: 'success'
                  }
                ]}
              /> */}
            </Box>
            
            <AnalyticsFilters
              title="Revenue Analytics Filters"
              analyticsType="revenue"
              timeRange={timeRange}
              onTimeRangeChange={handleChangeTimeInterval}
              isCustomSelect={isCustomSelect}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              dateOptions={dateDD || []}
              onApply={() => fetchAnalyticsData('revenue', { partnerId })}
              buttonSize="small"
              analyticsTypes={analyticsTypes?.data?.analyticsTypes || []}
              analyticsMetadata={analyticsMetadata?.data || {}}
              partnerId={partnerId}
              onPartnerChange={handlePartnerChange}
            />
            
            {/* Enhanced KPI Cards - Small Size */}
            {metricsLoading || isLoading ? <Loader variant='skeleton' skeletonCount={4} skeletonVariant='kpi' /> : 
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Total Revenue"
                  value={formatOcpiCurrency(revenue?.dailyData?.totalRevenue || 0)}
                  icon="eva:trending-up-fill"
                  color="success"
                  isCurrency={true}
                  showTrend={true}
                  trendValue={`+${MOCK_DATA.trends.revenue.change}%`}
                  trendIcon="eva:trending-up-fill"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Our Share Revenue"
                  value={formatOcpiCurrency(revenue?.dailyData?.ourShare || 0)}
                  icon="eva:credit-card-fill"
                  color="primary"
                  isCurrency={true}
                  subtitle={`${MOCK_DATA.revenueAnalytics.avgShareRate}% of total`}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Total Transactions"
                  value={revenue?.dailyData?.totalTransactions || 0}
                  icon="eva:activity-fill"
                  color="info"
                  subtitle="Based on filters"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Avg Share Rate"
                  value={revenue?.dailyData?.avgShareRate || 0}
                  icon="eva:pie-chart-fill"
                  color="warning"
                  unit="%"
                  subtitle="Across all partners"
                />
              </Grid>
            </Grid>}

            {/* Revenue Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Monthly Revenue Trends */}
              <Grid item xs={12} lg={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Monthly Revenue Trends
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'line', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: (revenue?.charts?.data || []).map(d => d.month)
                        },
                        yaxis: {
                          title: { text: 'Revenue ($)' },
                          labels: { formatter: (value) => formatOcpiCurrency(value) }
                        },
                        stroke: { curve: 'smooth', width: 3 },
                        colors: [theme.palette.success.main, theme.palette.warning.main],
                        legend: { position: 'top' },
                        tooltip: {
                          y: {
                            formatter: (value) => formatOcpiCurrency(value)
                          }
                        }
                      }}
                      series={[
                        {
                          name: 'Total Revenue',
                          data: (revenue?.charts?.data || []).map(d => d.totalRevenue)
                        },
                        {
                          name: 'Our Share',
                          data: (revenue?.charts?.data || []).map(d => d.ourShare)
                        }
                      ]}
                      type="line"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Revenue by Type */}
              {/* <Grid item xs={12} lg={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Revenue by Type
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'donut', 
                          toolbar: { show: false },
                          height: 350
                        },
                        labels: (MOCK_DATA.revenueAnalytics.revenueByType || []).map(d => d.type),
                        colors: [theme.palette.success.main, theme.palette.primary.main, theme.palette.warning.main],
                        legend: { position: 'bottom' },
                        plotOptions: {
                          pie: {
                            donut: {
                              size: '60%',
                              labels: {
                                show: true,
                                total: {
                                  show: true,
                                  label: 'Total Revenue',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: theme.palette.text.primary
                                }
                              }
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (value, { seriesIndex }) => {
                            const data = (MOCK_DATA.revenueAnalytics.revenueByType || [])[seriesIndex];
                            return `${data.percentage}%\n$${data.revenue.toLocaleString()}`;
                          }
                        }
                      }}
                                              series={(MOCK_DATA.revenueAnalytics.revenueByType || []).map(d => d.revenue)}
                      type="donut"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid> */}
            </Grid>

            <AnalyticsTable
              title="Partner Revenue Performance"
              rows={revenue?.revenueAnalysis?.partners || []}
              columns={REVENUE_TABLE_COLUMNS(theme.palette.success.main, theme.palette.warning.main)}
            />
          </Box>
        );
      case "partnerTransactions":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Partner Transaction Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Detailed analysis of outgoing and incoming transactions with success rates across all partners.
            </Typography>
            
            <AnalyticsFilters
              title="Partner Transaction Analytics Filters"
              analyticsType="partners"
              partners={MOCK_DATA.partnerTransactions.byPartner || []}
              timeRange={timeRange}
              onTimeRangeChange={handleChangeTimeInterval}
              isCustomSelect={isCustomSelect}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              dateOptions={dateDD || []}
              onApply={() => fetchAnalyticsData(['partners', 'transactions'], { partnerId })}
              buttonSize="small"
              analyticsTypes={analyticsTypes?.data?.analyticsTypes || []}
              analyticsMetadata={analyticsMetadata?.data || {}}
              partnerId={partnerId}
              onPartnerChange={handlePartnerChange}
            />
            
            {/* Transaction Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', mr: 2 }}>
                        <Icon icon="eva:arrow-up-fill" />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" color="primary.main">
                          Outgoing Transactions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Transactions sent to partners
                        </Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.outgoing.total.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.outgoing.successful.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Successful
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.outgoing.failed.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Failed
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Icon icon="eva:trending-up-fill" color="success.main" width={16} height={16} />
                      <Typography variant="body2" color="success.main" sx={{ ml: 1 }}>
                        Success Rate: {MOCK_DATA.partnerTransactions.outgoing.successRate}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.lighter', color: 'secondary.main', mr: 2 }}>
                        <Icon icon="eva:arrow-down-fill" />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" color="secondary.main">
                          Incoming Transactions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Transactions received from partners
                        </Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.incoming.total.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.incoming.successful.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Successful
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                          {MOCK_DATA.partnerTransactions.incoming.failed.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Failed
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Icon icon="eva:trending-up-fill" color="success.main" width={16} height={16} />
                      <Typography variant="body2" color="success.main" sx={{ ml: 1 }}>
                        Success Rate: {MOCK_DATA.partnerTransactions.incoming.successRate}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Monthly Trends Chart */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Monthly Transaction Trends
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'line', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: (MOCK_DATA.partnerTransactions.monthlyTrends || []).map(d => d.month)
                        },
                        yaxis: {
                          title: { text: 'Transaction Count' },
                          labels: { formatter: (value) => value.toLocaleString() }
                        },
                        stroke: { curve: 'smooth', width: 3 },
                        colors: [theme.palette.primary.main, theme.palette.secondary.main],
                        legend: { position: 'top' },
                        tooltip: {
                          y: {
                            formatter: (value) => value.toLocaleString()
                          }
                        }
                      }}
                      series={[
                        {
                          name: 'Outgoing',
                          data: (MOCK_DATA.partnerTransactions.monthlyTrends || []).map(d => d.outgoing)
                        },
                        {
                          name: 'Incoming',
                          data: (MOCK_DATA.partnerTransactions.monthlyTrends || []).map(d => d.incoming)
                        }
                      ]}
                      type="line"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Transaction Types Pie Chart */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Transaction Types Distribution
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'donut', 
                          toolbar: { show: false },
                          height: 350
                        },
                        labels: ['Start Transaction', 'Stop Transaction', 'Remote Start', 'Remote Stop'],
                        colors: [
                          (MOCK_DATA.partnerTransactions.transactionTypes?.startTransaction?.color || '#00C851'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.stopTransaction?.color || '#FF6900'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.remoteStart?.color || '#0074D9'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.remoteStop?.color || '#9933CC')
                        ],
                        legend: { 
                          position: 'bottom',
                          fontSize: '12px'
                        },
                        plotOptions: {
                          pie: {
                            donut: {
                              size: '60%',
                              labels: {
                                show: true,
                                total: {
                                  show: true,
                                  label: 'Total Transactions',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: theme.palette.text.primary
                                }
                              }
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (value, { seriesIndex }) => {
                            const types = ['startTransaction', 'stopTransaction', 'remoteStart', 'remoteStop'];
                            const type = types[seriesIndex];
                            const data = MOCK_DATA.partnerTransactions.transactionTypes?.[type];
                            return data ? `${data.count.toLocaleString()}\n(${data.successRate}%)` : 'N/A';
                          },
                          style: {
                            fontSize: '11px',
                            fontWeight: 600
                          }
                        },
                        tooltip: {
                          y: {
                            formatter: (value, { seriesIndex }) => {
                              const types = ['startTransaction', 'stopTransaction', 'remoteStart', 'remoteStop'];
                              const type = types[seriesIndex];
                              const data = MOCK_DATA.partnerTransactions.transactionTypes?.[type];
                              return data ? `${data.count.toLocaleString()} transactions\nSuccess Rate: ${data.successRate}%` : 'N/A';
                            }
                          }
                        }
                      }}
                      series={[
                        MOCK_DATA.partnerTransactions.transactionTypes?.startTransaction?.count || 0,
                        MOCK_DATA.partnerTransactions.transactionTypes?.stopTransaction?.count || 0,
                        MOCK_DATA.partnerTransactions.transactionTypes?.remoteStart?.count || 0,
                        MOCK_DATA.partnerTransactions.transactionTypes?.remoteStop?.count || 0
                      ]}
                      type="donut"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Charts Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Success Rate Comparison Chart */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Success Rate Comparison
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'bar', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: ['Outgoing', 'Incoming']
                        },
                        yaxis: {
                          title: { text: 'Success Rate (%)' },
                          max: 100
                        },
                        colors: [theme.palette.primary.main, theme.palette.secondary.main],
                        plotOptions: {
                          bar: { 
                            borderRadius: 4,
                            borderRadius: 4,
                            horizontal: true
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (value) => `${value}%`
                        }
                      }}
                      series={[{
                        name: 'Success Rate',
                        data: [
                          MOCK_DATA.partnerTransactions.outgoing.successRate,
                          MOCK_DATA.partnerTransactions.incoming.successRate
                        ]
                      }]}
                      type="bar"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Transaction Types Success Rate Chart */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Transaction Types Success Rates
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'bar', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: ['Start Transaction', 'Stop Transaction', 'Remote Start', 'Remote Stop']
                        },
                        yaxis: {
                          title: { text: 'Success Rate (%)' },
                          max: 100
                        },
                        colors: [
                          (MOCK_DATA.partnerTransactions.transactionTypes?.startTransaction?.color || '#00C851'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.stopTransaction?.color || '#FF6900'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.remoteStart?.color || '#0074D9'),
                          (MOCK_DATA.partnerTransactions.transactionTypes?.remoteStop?.color || '#9933CC')
                        ],
                        plotOptions: {
                          bar: { 
                            borderRadius: 4,
                            distributed: true
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (value) => `${value}%`,
                          style: {
                            fontSize: '12px',
                            fontWeight: 600
                          }
                        }
                      }}
                      series={[{
                        name: 'Success Rate',
                        data: [
                          MOCK_DATA.partnerTransactions.transactionTypes?.startTransaction?.successRate || 0,
                          MOCK_DATA.partnerTransactions.transactionTypes?.stopTransaction?.successRate || 0,
                          MOCK_DATA.partnerTransactions.transactionTypes?.remoteStart?.successRate || 0,
                          MOCK_DATA.partnerTransactions.transactionTypes?.remoteStop?.successRate || 0
                        ]
                      }]}
                      type="bar"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Partner Performance Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Partner Transaction Performance
                </Typography>

                <AnalyticsTable
                  title="Partner Transaction Performance"
                  rows={(MOCK_DATA.partnerTransactions.byPartner || []).map((partner, index) => {
                    const overallSuccess = (
                      ((partner.outgoing.successful + partner.incoming.successful) / 
                       (partner.outgoing.total + partner.incoming.total)) * 100
                    ).toFixed(1);
                    
                    return {
                      id: index,
                      partner: partner.name,
                      outgoingTotal: partner.outgoing.total.toLocaleString(),
                      outgoingSuccess: `${partner.outgoing.successRate}% (${partner.outgoing.successful.toLocaleString()}/${partner.outgoing.total.toLocaleString()})`,
                      incomingTotal: partner.incoming.total.toLocaleString(),
                      incomingSuccess: `${partner.incoming.successRate}% (${partner.incoming.successful.toLocaleString()}/${partner.incoming.total.toLocaleString()})`,
                      overallSuccess: `${overallSuccess}%`
                    };
                  })}
                  columns={PARTNER_TRANSACTIONS_TABLE_COLUMNS}
                />

                </CardContent>
              </Card>
            </Box>
          );
      case "roaming":
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Roaming Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive analysis of roaming sessions, energy consumption, and location performance.
                </Typography>
              </Box>
              {/* <ReportDownload
                reportType="roaming"
                reportTitle="Download Roaming Report"
                downloadFunction={handleReportDownload}
                isDownloading={isDownloadingReport}
                downloadError={downloadError}
                filters={{
                  timeRange,
                  customStartDate,
                  customEndDate,
                  partnerId
                }}
                buttonVariant="outlined"
                buttonSize="small"
                iconOnly={false}
              /> */}
            </Box>
            
            <AnalyticsFilters
              title="Roaming Analytics Filters"
              analyticsType="roaming"
              timeRange={timeRange}
              onTimeRangeChange={handleChangeTimeInterval}
              isCustomSelect={isCustomSelect}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              dateOptions={dateDD || []}
              onApply={() => fetchAnalyticsData('roaming', { partnerId })}
              buttonSize="small"
              analyticsTypes={analyticsTypes?.data?.analyticsTypes || []}
              analyticsMetadata={analyticsMetadata?.data || {}}
              partnerId={partnerId}
              onPartnerChange={handlePartnerChange}
            />
            
            {/* Roaming KPI Cards */}
            {metricsLoading || isLoading ? 
            <Loader variant='skeleton' skeletonCount={4} skeletonVariant='kpi' /> : 
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Roaming Sessions"
                  value={roaming?.kpis?.roamingSessions || 0}
                  icon="eva:globe-fill"
                  color="primary"
                  showTrend={true}
                  trendValue={`+${roaming?.trends?.roamingSessions?.change || 0}%`}
                  trendIcon={`eva:trending-${roaming?.trends?.roamingSessions?.direction}-fill`}
                  subtitle="Total roaming sessions"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Active Tokens"
                  value={roaming?.kpis?.activeTokens || 0}
                  icon="mdi:key"
                  color="success"
                  showTrend={true}
                  trendValue={`+${roaming?.trends?.activeTokens?.change || 0}%`}
                  trendIcon={`eva:trending-${roaming?.trends?.activeTokens?.direction}-fill`}
                  subtitle="Active roaming tokens"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Energy (kWh)"
                  value={roaming?.kpis?.totalEnergy || 0}
                  icon="eva:flash-fill"
                  color="warning"
                  unit=" kWh"
                  showTrend={true}
                  trendValue={`+${roaming?.trends?.energy?.change || 0}%`}
                  trendIcon={`eva:trending-${roaming?.trends?.energy?.direction}-fill`}
                  subtitle="Total energy consumed"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKPICard
                  title="Avg Utilization"
                  value={roaming?.kpis?.avgUtilization || 0}
                  icon="eva:pie-chart-fill"
                  color="info"
                  unit="%"
                  showTrend={true}
                  trendValue={`+${roaming?.trends?.utilization?.change || 0}%`}
                  trendIcon={`eva:trending-${roaming?.trends?.utilization?.direction}-fill`}
                  subtitle="Average station utilization"
                />
              </Grid>
            </Grid>
}
            {/* Roaming Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Roaming Activity Trends */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Roaming Activity Trends
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'line', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: (roaming?.charts?.roamingActivityTrends?.data || []).map(d => d.month)
                        },
                        yaxis: {
                          title: { text: 'Count' },
                          labels: { formatter: (value) => value.toLocaleString() }
                        },
                        stroke: { curve: 'smooth', width: 3 },
                        colors: [theme.palette.primary.main, theme.palette.success.main],
                        legend: { position: 'top' },
                        tooltip: {
                          y: {
                            formatter: (value) => value.toLocaleString()
                          }
                        }
                      }}
                      series={[
                        {
                          name: 'Sessions',
                          data: (roaming?.charts?.roamingActivityTrends?.data || []).map(d => d.roamingSessions)
                        },
                        {
                          name: 'Tokens',
                          data: (roaming?.charts?.roamingActivityTrends?.data || []).map(d => d.totalTokens)
                        }
                      ]}
                      type="line"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Energy Consumption Chart */}
              <Grid item xs={12} lg={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Energy Consumption Chart
                    </Typography>
                    <Chart
                      options={{
                        chart: { 
                          type: 'area', 
                          toolbar: { show: false },
                          height: 350
                        },
                        xaxis: {
                          categories: (roaming?.charts?.energyConsumption?.data || []).map(d => d?.month || '')
                        },
                        yaxis: [
                          {
                            title: { text: 'Energy (kWh)' },
                            labels: { formatter: (value) => value }
                          },
                        ],
                        stroke: { curve: 'smooth', width: 3 },
                        colors: [theme.palette.warning.main, theme.palette.info.main],
                        legend: { position: 'top' },
                        fill: {
                          type: 'gradient',
                          gradient: {
                            shadeIntensity: 1,
                            opacityFrom: 0.7,
                            opacityTo: 0.1,
                            stops: [0, 100]
                          }
                        },
                        tooltip: {
                          y: [
                            {
                              formatter: (value) => `${(value || 0).toLocaleString()} kWh`
                            },
                          ]
                        }
                      }}
                      series={[
                        {
                          name: 'Energy Consumption',
                          type: 'area',
                          data: (roaming?.charts?.energyConsumption?.data || []).map(d => d?.energyKwh || 0)
                        },
                      ]}
                      type="area"
                      height={350}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Top Performing Locations */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Performing Locations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Locations ranked by total sessions, average duration, and utilization percentage
                </Typography>

                <AnalyticsTable
                  title="Top Performing Locations"
                  rows={roaming?.topLocations || []}
                  columns={TOP_LOCATIONS_TABLE_COLUMNS}
                />
              </CardContent>
            </Card>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            OCPI Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive insights into your OCPI operations, revenue, and partner performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ReportDownload
            reportType="comprehensive"
            reportTitle="Download Comprehensive Report"
            downloadFunction={handleReportDownload}
            isDownloading={isDownloadingReport}
            downloadError={downloadError}
            filters={{
              timeRange,
              customStartDate,
              customEndDate,
              partnerId
            }}
            buttonSx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          />
        </Box>
      </Box>

      {/* Top Section - KPI Cards */}
      {isLoading ? <Loader variant='skeleton' skeletonCount={4} skeletonVariant='kpi' /> : 
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsKPICard
            title="Revenue"
            value={formatOcpiCurrency(dashboard?.kpis?.totalRevenue || 0)}
            icon="eva:credit-card-fill"
            color="success"
            isCurrency={true}
            showTrend={true}
            trendValue={`+${dashboard?.trends?.revenue?.change || 0}%`}
            trendIcon={`eva:trending-${dashboard?.trends?.revenue?.direction}-fill`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsKPICard
            title="Sessions"
            value={dashboard?.kpis?.totalTransactions || 0}
            icon="eva:activity-fill"
            color="primary"
            showTrend={true}
            trendValue={`+${dashboard?.trends?.transactions?.change || 0}%`}
            trendIcon={`eva:trending-${dashboard?.trends?.sessions?.direction}-fill`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsKPICard
            title="Active Partners"
            value={dashboard?.kpis?.activePartners || 0}
            icon="eva:people-fill"
            color="warning"
            showTrend={true}
            trendValue={`+${dashboard?.trends?.partners?.change || 0}%`}
            trendIcon={`eva:trending-${dashboard?.trends?.partners?.direction}-fill`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsKPICard
            title="Energy"
            value={dashboard?.kpis?.totalEnergy || 0}
            icon="eva:flash-fill"
            color="secondary"
            unit=" kWh"
            showTrend={true}
            trendValue={`+${dashboard?.trends?.energy?.change || 0}%`}
            trendIcon={`eva:trending-${dashboard?.trends?.energy?.direction}-fill`}
          />
        </Grid>
      </Grid>}

      {/* Middle Section - Tab Content */}
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          {ANALYTICS_TABS.map((tab) => (
            <Tab
              key={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon={tab.icon} />
                  {tab.label}
                </Box>
              }
              value={tab.value}
            />
          ))}
        </Tabs>
        <Box sx={{ p: 3 }}>
          {renderTabContent()}
        </Box>
      </Paper>
    </Container>
  );
};

export default OCPIAnalytics;
